import React, {
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import EditTestCaseBreadCrumbs from "../EditTestCaseBreadCrumbs";

import tw, { styled } from "twin.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import "styled-components/macro";
import {
  Group,
  TestCase,
  GroupPopulation,
  HapiOperationOutcome,
  PopulationExpectedValue,
  MeasureErrorType,
  Model,
  ValidationStatus,
} from "@madie/madie-models";
import useTestCaseServiceApi from "../../../api/useTestCaseServiceApi";
import Editor from "../../editor/Editor";
import { TestCaseValidator } from "../../../validators/TestCaseValidator";
import { MadieError, sanitizeUserInput } from "../../../util/Utils";
import TestCaseSeries from "../../createTestCase/TestCaseSeries";
import _ from "lodash";
import {
  FHIR_POPULATION_CODES,
  mapExistingTestCasePopulations,
  getPopulationTypesForScoring,
  triggerPopChanges,
} from "../../../util/PopulationsMap";
import calculationService, {
  PopulationEpisodeResult,
} from "../../../api/CalculationService";
import {
  CalculationOutput,
  DetailedPopulationGroupResult,
} from "fqm-execution/build/types/Calculator";
import {
  measureStore,
  routeHandlerStore,
  useDocumentTitle,
  checkUserCanEdit,
  useFeatureFlags,
} from "@madie/madie-util";
import useExecutionContext from "../../routes/qiCore/useExecutionContext";
import { MadieEditor } from "@madie/madie-editor";
import CreateTestCaseRightPanelNavTabs from "../../createTestCase/CreateTestCaseRightPanelNavTabs";
import CreateTestCaseLeftPanelNavTabs from "../../createTestCase/CreateTestCaseLeftPanelNavTabs";
import ExpectedActual from "../../createTestCase/RightPanel/ExpectedActual/ExpectedActual";
import "./EditTestCase.scss";
import "allotment/dist/style.css";
import CalculationResults from "./calculationResults/CalculationResults";
import {
  Button,
  TextField,
  MadieAlert,
  MadieSpinner,
  MadieDiscardDialog,
  Toast,
  TextArea,
} from "@madie/madie-design-system/dist/react";
import FileUploader from "../../fileUploader/FileUploader";
import { ScanValidationDto } from "../../../api/models/ScanValidationDto";
import { Bundle } from "fhir/r4";
import { Allotment } from "allotment";
import ElementsTab from "./LeftPanel/ElementsTab/ElementsTab";
import { QiCoreResourceProvider } from "../../../util/QiCorePatientProvider";
import { CqlDefinitionCallstack } from "../groupCoverage/QiCoreGroupCoverage";
import useFhirCqlParsingService from "../../../api/cqlElmTranslationService/useFhirCqlParsingService";
import checkSpecialCharacters from "../../../util/checkSpecialCharacters";
import EditorSearch from "./LeftPanel/EditorSearch";
import useFormikResetOnEvent from "../../../../../common/useFormikResetOnEvent";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  extractValidationErrorsFromOutcome,
  isHapiOutcomeIssueCodeInformational,
} from "./EditTestCaseUtil";
import { useTestCasePolling } from "../../../hooks/useTestCasePolling";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import ValidationPanel from "./ValidationPanel";
import ValidationStatusIcon from "./ValidationStatusIcon";

const TestCaseForm = tw.form`m-3`;

interface AlertProps {
  status?: "success" | "warning" | "error" | "info" | "meta" | null;
  message?: any;
}

interface navigationParams {
  id: string;
  measureId: string;
}

const styles = {
  success: `color #333333; background-color: #90EE90; border: solid 1px #7cb342`,
  warning: `color #333333; background-color: #FFF9EB; border: solid 1px #FFC438`,
  error: `color #333333; background-color: #FDE7EA; border: solid 1px #D92F2F`,
  // meta and default are same colors.
  meta: `color #333333; background-color: #e6f5ff; border: solid 1px #0073c8`,
  default: `color #333333; background-color: #e6f5ff; border: solid 1px #0073c8`,
};

/*
previous color system based off of tw.
success #249A5B vs #A4FAA8, 3:1 fail dark green to light green
warning #B87A06 vs #FCEB9D, 3:1 fail, orange yellow
error: #BA1C32 vs #FBC4AB 4.1 fail red to red orange
// meta: 000 #b0EEFF: pass
default: #2469B7 vs #b0EEFF  d-L  teal 4.4:1
*/

const Alert = styled.div<AlertProps>(({ status = "default" }) => [
  styles[status],
  tw`rounded-lg p-2 m-2 text-base inline-flex items-center w-11/12`,
]);

const testCaseSeriesStyles = {
  border: "1px solid #8c8c8c",
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "3px",
    "& legend": {
      width: 0,
    },
  },
  "& .MuiOutlinedInput-root": {
    padding: 0,
  },
};

/*
For population values...
If testCase is present, then take the population groups off the testCase
If no testCase is present, then show the populations for the measure groups
coming from the loaded measure
 */

export function isEmptyTestCaseJsonString(
  jsonString: string | undefined | null
) {
  try {
    return (
      _.isNil(jsonString) ||
      _.isEmpty(jsonString.trim()) ||
      _.isEmpty(JSON.parse(jsonString))
    );
  } catch (error) {
    return true;
  }
}

export function findEpisodeActualValue(
  populationEpisodeResults: PopulationEpisodeResult[],
  populationValue: PopulationExpectedValue,
  populationDefinition: string
): number {
  const groupEpisodeResult = populationEpisodeResults?.find(
    (popEpResult) =>
      FHIR_POPULATION_CODES[popEpResult.populationType] ===
        populationValue.name && populationDefinition === popEpResult.define
  );
  return _.isNil(groupEpisodeResult) ? 0 : groupEpisodeResult.value;
}

const INITIAL_VALUES = {
  title: "",
  description: "",
  series: "",
  groupPopulations: [],
} as TestCase;

export interface EditTestCaseProps {
  errors: Array<string>;
  setErrors: (value: Array<string>) => void;
}

const EditTestCase = (props: EditTestCaseProps) => {
  useDocumentTitle("MADiE Edit Measure Edit Test Case");
  const navigate = useNavigate();
  const featureFlags = useFeatureFlags();
  const { measureId, id } = useParams<
    keyof navigationParams
  >() as navigationParams;
  // Avoid infinite dependency render. May require additional error handling for timeouts.
  const testCaseService = useRef(useTestCaseServiceApi());
  const calculation = useRef(calculationService());
  const fhirCqlParsingService = useRef(useFhirCqlParsingService());
  const [alert, setAlert] = useState<AlertProps>(null);
  const { errors, setErrors } = props;
  // Toast utilities
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<ReactNode>("");
  const [toastType, setToastType] = useState<string>("danger");
  const onToastClose = () => {
    setToastMessage("");
    setToastOpen(false);
  };

  const showToast = (
    message: ReactNode,
    toastType: "success" | "danger" | "warning" | "info"
  ) => {
    setToastOpen(true);
    setToastType(toastType);
    setToastMessage(message);
  };

  const [testCase, setTestCase] = useState<TestCase>(null);
  const [editorVal, setEditorVal]: [string, Dispatch<SetStateAction<string>>] =
    useState("Loading...");
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [seriesState, setSeriesState] = useState<any>({
    loaded: false,
    series: [],
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [populationGroupResults, setPopulationGroupResults] =
    useState<DetailedPopulationGroupResult[]>();
  const [calculationErrors, setCalculationErrors] = useState<AlertProps>();
  const [rightPanelActiveTab, setRightPanelActiveTab] =
    useState<string>("measurecql");
  const [leftPanelActiveTab, setLeftPanelActiveTab] =
    useState<string>("elements");
  const [groupPopulations, setGroupPopulations] = useState<GroupPopulation[]>(
    []
  );
  const [callstackMap, setCallstackMap] = useState<CqlDefinitionCallstack>();

  const {
    measureState,
    bundleState,
    valueSetsState,
    executionContextReady,
    executing,
    setExecuting,
  } = useExecutionContext();
  const [measure] = measureState;
  const [measureBundle] = bundleState;
  const [valueSets] = valueSetsState;
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const { updateMeasure } = measureStore;

  const canEdit = checkUserCanEdit(
    measure?.measureSet?.owner,
    measure?.measureSet?.acls,
    measure?.measureMetaData?.draft,
    featureFlags?.EditTestsOnVersionedMeasures
  );

  const formik = useFormik({
    initialValues: { ...INITIAL_VALUES },
    validationSchema: TestCaseValidator,
    // enableReinitialize: true,
    onSubmit: async (values: TestCase) => await handleSubmit(values),
  });
  useFormikResetOnEvent(formik);
  const { resetForm } = formik;

  //stu6 validation logic required
  const [validationSchema, setValidationSchema] = useState({});
  const [initialFormikValuesStu6, setInitialFormikValuesStu6] = useState({});

  const formikStu6Context = useFormik({
    initialValues: initialFormikValuesStu6,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });
  useFormikResetOnEvent(formikStu6Context);

  const [shouldPoll, setShouldPoll] = useState(false);

  const isQiCoreV6 = measure?.model === "QI-Core v6.0.0";
  // testCase polling, gets triggered when validationStatus is either Pending or Validating
  useTestCasePolling({
    testCaseId: id,
    measureId,
    shouldStart: shouldPoll,
    onUpdate: (updatedTc: TestCase) => {
      const nextTc = _.cloneDeep(updatedTc);
      handleHapiOutcome(nextTc?.hapiOperationOutcome);
    },
    validateTest: !isQiCoreV6,
    onError: (error) => {
      showToast(error, "danger");
    },
  });

  //needs to be added to feature flag config once the feature flags are moved to Util
  const testCaseAlertToast = false;
  useEffect(() => {
    if (_.isNil(populationGroupResults) || _.isEmpty(populationGroupResults)) {
      setGroupPopulations(_.cloneDeep(formik.values.groupPopulations));
    } else {
      setGroupPopulations(
        _.cloneDeep(
          calculation.current.processTestCaseResults(
            formik.values,
            measure.groups,
            populationGroupResults
          )?.groupPopulations
        )
      );
    }
  }, [formik.values.groupPopulations, populationGroupResults]);

  const mapMeasureGroup = (group: Group): GroupPopulation => {
    const calculateEpisodes = group.populationBasis === "boolean";
    const populationValues = getPopulationTypesForScoring(group)?.map(
      (population: PopulationExpectedValue) => ({
        name: population.name,
        expected: calculateEpisodes ? false : null,
        actual: calculateEpisodes ? false : null,
        id: population.id,
        criteriaReference: population.criteriaReference,
      })
    );
    return {
      groupId: group.id,
      scoring: group.scoring,
      populationBasis: group.populationBasis,
      populationValues,
      stratificationValues: group.stratifications
        ?.filter((stratification) => stratification.cqlDefinition)
        ?.map((stratification, index) => ({
          name: `Strata ${index + 1}`,
          expected: calculateEpisodes ? false : null,
          actual: calculateEpisodes ? false : null,
          id: stratification.id,
          criteriaReference: "",
          populationValues: populationValues?.map((pv) => ({ ...pv })), // Return a copy of populationValues
        })),
    };
  };

  const mapMeasureGroups = useCallback(
    (measureGroups: Group[]): GroupPopulation[] => {
      return measureGroups.map(mapMeasureGroup);
    },
    []
  );

  const { updateRouteHandlerState } = routeHandlerStore;
  useEffect(() => {
    updateRouteHandlerState({
      canTravel: !formik.dirty && !isJsonModified() && !formikStu6Context.dirty, // both formik.dirty fields are required
      pendingRoute: "",
    });
  }, [formik.dirty, editorVal, testCase?.json, formikStu6Context.dirty]);

  const standardizeJson = (testCase) => {
    try {
      if (JSON.parse(testCase.json)) {
        return JSON.stringify(JSON.parse(testCase.json), null, 2);
      }
    } catch (e) {
      setErrors([
        ...errors,
        "Test Case JSON contains a syntax error. Test case can be saved, but not run.",
      ]);
      return testCase?.json;
    }
  };

  const loadTestCase = () => {
    testCaseService.current
      .getTestCase(id, measureId, !isQiCoreV6)
      .then((tc: TestCase) => {
        const nextTc = _.cloneDeep(tc);
        nextTc.json = standardizeJson(nextTc);
        setTestCase(nextTc);
        setEditorVal(nextTc.json ? nextTc.json : "");
        if (measure && measure.groups) {
          nextTc.groupPopulations = measure.groups.map((group) => {
            const existingGroupPop = tc.groupPopulations?.find(
              (gp) => gp.groupId === group.id
            );
            return _.isNil(existingGroupPop)
              ? mapMeasureGroup(group)
              : {
                  ...mapExistingTestCasePopulations(existingGroupPop, group),
                  populationBasis: group?.populationBasis,
                };
          });
        } else {
          nextTc.groupPopulations = [];
        }
        resetForm({ values: _.cloneDeep(nextTc) });
        handleHapiOutcome(nextTc?.hapiOperationOutcome);
        if (
          [ValidationStatus.PENDING, ValidationStatus.VALIDATING].includes(
            tc.validationStatus
          )
        ) {
          setShouldPoll(true);
        } else {
          setShouldPoll(false);
        }
      })
      .catch((error) => {
        if (error.toString().includes("404")) {
          navigate("/404");
        }
      });
  };

  useEffect(() => {
    if (!seriesState.loaded) {
      testCaseService.current
        .getTestCaseSeriesForMeasure(measureId)
        .then((existingSeries) =>
          setSeriesState({ loaded: true, series: existingSeries })
        )
        .catch((error) => {
          showToast(error.message, "danger");
          setErrors([...errors, error.message]);
        });
    }

    if (id && measure) {
      loadTestCase();
    }

    if (_.isNil(callstackMap) && measure?.cql) {
      fhirCqlParsingService.current
        .getDefinitionCallstacks(measure.cql)
        .then((callstack: CqlDefinitionCallstack) => {
          setCallstackMap(callstack);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [
    id,
    measureId,
    testCaseService,
    setTestCase,
    resetForm,
    measure,
    mapMeasureGroups,
    seriesState.loaded,
  ]);

  const handleSubmit = async (testCase: TestCase) => {
    setAlert(null);
    testCase.title = sanitizeUserInput(testCase.title);
    testCase.description = sanitizeUserInput(testCase.description);
    testCase.series = sanitizeUserInput(testCase.series);

    if (id) {
      return await updateTestCase(testCase);
    }
    await createTestCase(testCase);
  };

  const createTestCase = async (testCase: TestCase) => {
    try {
      testCase.json = editorVal || null;
      const savedTestCase = await testCaseService.current.createTestCase(
        testCase,
        measureId
      );
      setEditorVal(savedTestCase.json);

      handleTestCaseResponse(savedTestCase, "create", false);
    } catch (error) {
      showToast("An error occurred while creating the test case.", "danger");
      setErrors([...errors, "An error occurred while creating the test case."]);
    }
  };

  const convertDatesToUTC = () => {
    try {
      const parsedValue = JSON.parse(editorVal);
      let timezoneUpdated = false;
      dayjs.extend(utc);
      const regex =
        /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2}|[+-]\d{4})/;
      const updatedData = JSON.stringify(parsedValue, (key, value) => {
        if (typeof value === "string" && regex.test(value)) {
          //overwrite timezones
          const newValue = dayjs(
            value.replace(/([+-]\d{2}:\d{2}|Z)$/, "+00:00")
          )
            .utc()
            .toISOString();
          if (value != newValue) {
            timezoneUpdated = true;
          }
          return newValue;
        }
        return value;
      });
      return { json: updatedData, isTimezoneUpdated: timezoneUpdated };
    } catch (error) {
      console.error("Error parsing or converting dates:", error);
    }
  };

  const updateTestCase = async (testCase: TestCase) => {
    const errorMsg = checkSpecialCharacters(testCase);
    if (errorMsg) {
      showToast(errorMsg, "danger");
      return;
    }
    let timezoneUpdated = false;
    try {
      const updatedValue = convertDatesToUTC();
      if (updatedValue) {
        testCase.json = updatedValue.json;
        timezoneUpdated = updatedValue.isTimezoneUpdated;
      } else {
        testCase.json = editorVal;
      }

      setValidationErrors(() => []);
      const updatedTestCase = await testCaseService.current.updateTestCase(
        testCase,
        measureId
      );
      // initiate polling for validation response
      if (updatedTestCase.validationStatus === ValidationStatus.PENDING) {
        setShouldPoll(true);
      } else {
        setShouldPoll(false);
      }
      const updatedTc = _.cloneDeep(updatedTestCase);
      updatedTc.json = standardizeJson(updatedTc);
      resetForm({
        values: _.cloneDeep(updatedTc),
      });
      setTestCase(_.cloneDeep(updatedTc));
      setEditorVal(updatedTc.json);
      handleTestCaseResponse(updatedTc, "update", timezoneUpdated);
    } catch (error) {
      showToast(
        error instanceof MadieError
          ? error.message
          : "An error occurred while updating the test case.",
        "danger"
      );
      setErrors([...errors, "An error occurred while updating the test case."]);
    }
  };

  const calculate = async (e) => {
    e.preventDefault();
    setExecuting(true);
    setErrors([]);
    setPopulationGroupResults(() => undefined);
    if (measure && measure.cqlErrors) {
      setCalculationErrors({
        status: "warning",
        message:
          "Cannot execute test case while errors exist in the measure CQL.",
      });
      return;
    }
    setValidationErrors(() => []);
    let modifiedTestCase = { ...testCase };
    if (isJsonModified()) {
      modifiedTestCase.json = editorVal;
      try {
        // Validate test case JSON prior to execution
        const validationResult =
          await testCaseService.current.validateTestCaseBundle(
            JSON.parse(editorVal),
            measure.model
          );
        const errors = handleHapiOutcome(validationResult);
        if (!_.isNil(errors) && errors.length > 0 && hasErrorSeverity(errors)) {
          setCalculationErrors({
            status: "warning",
            message:
              "Test case execution was aborted due to errors with the test case JSON.",
          });
          return;
        }
      } catch (error) {
        setCalculationErrors({
          status: "error",
          message:
            "Test case execution was aborted because JSON could not be validated. If this error persists, please contact the help desk.",
        });
        return;
      } finally {
        setExecuting(false);
      }
    }

    try {
      const calculationOutput: CalculationOutput<any> =
        await calculation.current.calculateTestCases(
          measure,
          [modifiedTestCase],
          measureBundle,
          valueSets
        );

      const executionResults = calculationOutput.results;
      const validationResult =
        await testCaseService.current.validateTestCaseBundle(
          JSON.parse(editorVal),
          measure.model
        );
      handleHapiOutcome(validationResult);
      setCalculationErrors(undefined);
      setPopulationGroupResults(
        executionResults[0].detailedResults as DetailedPopulationGroupResult[]
      );
    } catch (error) {
      setCalculationErrors({
        status: "error",
        message: error.message,
      });
      setErrors([...errors, error.message]);
    } finally {
      setExecuting(false);
    }
  };

  const discardChanges = () => {
    //To DO: need to optimize it as it is calling the backend
    loadTestCase();
    setDiscardDialogOpen(false);
  };

  function handleTestCaseResponse(
    testCase: TestCase,
    action: "create" | "update",
    timezoneUpdated: boolean
  ) {
    if (testCase && testCase.id) {
      const validationErrors =
        testCase?.hapiOperationOutcome?.outcomeResponse?.issue;
      if (testCase.validationStatus === ValidationStatus.PENDING) {
        if (timezoneUpdated) {
          showToast(
            <div>
              <h3>
                Test case {action}d successfully! Test case validation has
                started running, please continue working in MADiE.
              </h3>
              <ul>
                MADiE enforces a UTC (offset 0) timestamp format with mandatory
                millisecond precision. All timestamps with non-zero offsets have
                been overwritten to UTC, and missing milliseconds have been
                defaulted to '000'.
              </ul>
            </div>,
            "warning"
          );
        } else {
          showToast(
            `Test case ${action}d successfully! Test case validation has started running, please continue working in MADiE.`,
            "success"
          );
        }
      } else if (hasValidHapiOutcome(testCase)) {
        // TODO: Remove if-check once the stu6TestCaseValidation flag is removed
        if (timezoneUpdated) {
          showToast(
            <div>
              <h3>Test case {action}d successfully!</h3>
              <ul>
                MADiE enforces a UTC (offset 0) timestamp format with mandatory
                millisecond precision. All timestamps with non-zero offsets have
                been overwritten to UTC, and missing milliseconds have been
                defaulted to '000'.
              </ul>
            </div>,
            "warning"
          );
        } else {
          showToast(`Test case ${action}d successfully!`, "success");
        }
      } else {
        const valErrors = validationErrors?.map((error) => (
          <li>{error.diagnostics}</li>
        ));
        const message: ReactNode = testCaseAlertToast ? (
          <div>
            <h3>
              Changes {action}d successfully but the following{" "}
              {severityOfValidationErrors(validationErrors)}(s) were found
            </h3>
            {timezoneUpdated && (
              <ul>
                MADiE enforces a UTC (offset 0) timestamp format with mandatory
                millisecond precision. All timestamps with non-zero offsets have
                been overwritten to UTC, and missing milliseconds have been
                defaulted to '000'.
              </ul>
            )}
            <ul>{valErrors}</ul>
          </div>
        ) : (
          <div>
            <h3>
              Test case updated successfully with{" "}
              {severityOfValidationErrors(validationErrors)}s in JSON
            </h3>
            {timezoneUpdated && (
              <ul style={{ listStyle: "inside" }}>
                <li>
                  MADiE enforces a UTC (offset 0) timestamp format with
                  mandatory millisecond precision. All timestamps with non-zero
                  offsets have been overwritten to UTC, and missing milliseconds
                  have been defaulted to '000'.
                </li>
              </ul>
            )}
          </div>
        );
        let severity = severityOfValidationErrors(validationErrors);
        if (severity === "error") {
          severity = "danger";
        }
        //@ts-ignore
        showToast(message, severity);
        handleHapiOutcome(testCase.hapiOperationOutcome);
      }
      updateMeasureStore(action, testCase);
    } else {
      showToast(
        `An error occurred - ${action} did not return the expected successful result.`,
        "danger"
      );
      setErrors([
        ...errors,
        `An error occurred - ${action} did not return the expected successful result.`,
      ]);
    }
  }

  // we need to update measure store with created/updated test case to avoid stale state,
  // otherwise we'll lose testcase updates
  function updateMeasureStore(action: string, testCase: TestCase) {
    const measureCopy = Object.assign({}, measure);
    if (action === "update") {
      // for update action, find and remove stale test case from measure
      measureCopy.testCases = measureCopy.testCases?.filter(
        (tc) => tc.id !== testCase.id
      );
    }
    // add updated test to measure
    if (measureCopy.testCases) {
      measureCopy.testCases.push(testCase);
    } else {
      measureCopy.testCases = [testCase];
    }
    // update measure store
    updateMeasure(measureCopy);
  }

  // TODO: What's the diff between this function and "handleHapiOutcome"?
  // do we need both?
  function hasValidHapiOutcome(testCase: TestCase) {
    return (
      // Consider valid if no hapi outcome. Most likely the editor is empty.
      _.isNil(testCase.hapiOperationOutcome) ||
      ((testCase.hapiOperationOutcome.code === 200 ||
        testCase.hapiOperationOutcome.code === 201) &&
        isHapiOutcomeIssueCodeInformational(testCase?.hapiOperationOutcome))
    );
  }

  function handleHapiOutcome(outcome: HapiOperationOutcome) {
    const errors = extractValidationErrorsFromOutcome(outcome);
    setValidationErrors(errors);
    return errors;
  }

  function formikErrorHandler(name: string) {
    if (formik.touched[name] && formik.errors[name]) {
      return `${formik.errors[name]}`;
    }
  }

  function isModified() {
    if (testCase) {
      if (
        _.isNil(testCase?.json) &&
        !_.isNil(editorVal) &&
        _.isEmpty(editorVal.trim()) &&
        !formik.dirty
      ) {
        return false;
      } else {
        return (
          formik.isValid &&
          (formik.dirty ||
            editorVal !== testCase?.json ||
            !_.isEqual(
              _.cloneDeep(testCase?.groupPopulations),
              _.cloneDeep(formik.values.groupPopulations)
            ))
        );
      }
    } else {
      return formik.isValid && formik.dirty;
    }
  }

  function isJsonModified() {
    return testCase && (!_.isNil(testCase?.json) || !_.isEmpty(editorVal))
      ? editorVal !== testCase?.json
      : !isEmptyTestCaseJsonString(editorVal);
  }

  const hasErrorSeverity = (validationErrors) => {
    return (
      validationErrors.filter(
        (validationError) => validationError.severity === "error"
      ).length > 0
    );
  };

  // An empty string is also considered to be valid, as it is not malformed
  // and allows a user to edit for the first time
  const isValidJson = (str) => {
    if (!_.isEmpty(str) && "Loading..." !== str) {
      try {
        JSON.parse(str);
        return true;
      } catch (error) {
        return false;
      }
    }
    return true;
  };

  const isQICore6 = measure?.model === Model.QICORE_6_0_0;

  const severityOfValidationErrors = (validationErrors) => {
    const errorsWithNoSeverity = validationErrors?.filter(
      (validationError) => !validationError.hasOwnProperty("severity")
    ).length;
    const nonInformationalErrors = validationErrors?.filter(
      (validationError) =>
        /^information/.exec(validationError.severity) === null
    ).length;
    if (nonInformationalErrors > 0) {
      if (hasErrorSeverity(validationErrors) || errorsWithNoSeverity > 0) {
        return "error";
      }
      return "warning";
    }
    if (_.isNil(nonInformationalErrors)) {
      return "error";
    }
    return "info";
  };

  const readTestFileCb = (testCaseBundle: Bundle, errorMessage: string) => {
    if (errorMessage) {
      showToast(errorMessage, "danger");
    } else {
      setEditorVal(JSON.stringify(testCaseBundle, null, "\t"));
      showToast(
        "Test Case JSON copied into editor. QI-Core Defaults have been added. Please review and save your Test Case.",
        "success"
      );
    }
  };
  const updateTestCaseJson = (file) => {
    testCaseService.current
      .scanImportFile(file)
      .then((response: ScanValidationDto) => {
        if (response.valid) {
          testCaseService.current.readTestCaseFile(file, readTestFileCb);
        } else {
          showToast(response.error.defaultMessage, "danger");
        }
      })
      .catch((errors) => {
        showToast(
          "An error occurred while importing the test case, please try again. If the error persists, please contact the help desk.",
          "danger"
        );
      });
  };

  const allotmentRef = useRef(null);
  // we need to know which stratification rows to render
  const stratificationsMap = useMemo(() => {
    const map = {};
    if (measure?.groups) {
      measure.groups.forEach((group) => {
        if (Array.isArray(group?.stratifications)) {
          for (const strat of group.stratifications) {
            map[strat.id] = strat.associations;
          }
        }
      });
    }
    return map;
  }, [measure?.groups]);
  return (
    <>
      {isQICore6 && !featureFlags?.stu6TestCaseValidation && (
        <div id="status-handler">
          <MadieAlert
            type="warning"
            content={
              <div
                aria-live="polite"
                role="alert"
                data-testid={"terminology-validation-warning"}
              >
                <strong>Warning: </strong>
                Validations for QI-Core STU6 are Disabled. No validations will
                be displayed. Validation of your Test Case JSON can be performed
                using an alternative tool, such as the{" "}
                <a
                  href={"https://validator.fhir.org/"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  HL7 FHIR Validator
                </a>{" "}
                with the US-Core and QI-Core IGs selected.
              </div>
            }
            canClose={false}
          />
        </div>
      )}
      <TestCaseForm
        data-testid="create-test-case-form"
        id="edit-test-case-qi-core"
        onSubmit={formik.handleSubmit}
      >
        <EditTestCaseBreadCrumbs testCase={testCase} measureId={measureId} />
        <div className="allotment-wrapper">
          <Allotment
            ref={allotmentRef}
            defaultSizes={[48, 48, 4]}
            vertical={false}
          >
            <Allotment.Pane>
              <div className="nav-panel">
                {featureFlags?.qiCoreElementsTab && isQICore6 ? (
                  <>
                    <div className="tab-container">
                      <CreateTestCaseLeftPanelNavTabs
                        leftPanelActiveTab={leftPanelActiveTab}
                        setLeftPanelActiveTab={setLeftPanelActiveTab}
                        isQICore6={isQICore6}
                        dirty={formikStu6Context.dirty}
                      />
                    </div>
                    <QiCoreResourceProvider>
                      {leftPanelActiveTab === "elements" &&
                        isValidJson(editorVal) && (
                          <div className="panel-content">
                            <div
                              data-testid="elements-content"
                              id="elements-content"
                            >
                              <FormikProvider value={formikStu6Context}>
                                <ElementsTab
                                  setValidationSchema={setValidationSchema}
                                  setInitialFormikValuesStu6={
                                    setInitialFormikValuesStu6
                                  }
                                  setEditorVal={setEditorVal}
                                  canEdit={canEdit}
                                  editorVal={editorVal}
                                  testCase={testCase}
                                />
                              </FormikProvider>
                            </div>
                          </div>
                        )}
                      {leftPanelActiveTab === "elements" &&
                        !isValidJson(editorVal) && (
                          <div style={{ width: "98%" }}>
                            <MadieAlert
                              type="error"
                              content={
                                <div
                                  aria-live="polite"
                                  role="alert"
                                  data-testid="json-error-alert-div"
                                  style={{
                                    paddingTop: "10px",
                                    paddingBottom: "8px",
                                  }}
                                >
                                  <h3>JSON Failing</h3>
                                  All JSON errors must be cleared before the UI
                                  Builder can be used.
                                </div>
                              }
                              alertProps={{
                                "data-testid": "json-error-alert",
                              }}
                              canClose={false}
                            />
                          </div>
                        )}
                      {leftPanelActiveTab === "json" && (
                        <Editor
                          onChange={(val: string) => setEditorVal(val)}
                          value={editorVal}
                          readOnly={!canEdit || _.isNil(testCase)}
                          height="100%"
                        />
                      )}
                    </QiCoreResourceProvider>
                  </>
                ) : (
                  <>
                    <div tw="float-right mr-4">
                      <EditorSearch />
                    </div>
                    <Editor
                      onChange={(val: string) => setEditorVal(val)}
                      value={editorVal}
                      readOnly={!canEdit || _.isNil(testCase)}
                      height="100%"
                    />
                  </>
                )}
              </div>
            </Allotment.Pane>
            <Allotment.Pane>
              <div className="right-panel">
                <CreateTestCaseRightPanelNavTabs
                  rightPanelActiveTab={rightPanelActiveTab}
                  setRightPanelActiveTab={setRightPanelActiveTab}
                />
                {rightPanelActiveTab === "measurecql" &&
                  (!measure?.cqlErrors ? (
                    <div
                      data-testid="test-case-cql-editor"
                      id="test-case-cql-editor"
                      style={{ height: "calc(100% - 24px)" }}
                    >
                      <MadieEditor
                        value={measure?.cql}
                        height="100%"
                        readOnly={true}
                        validationsEnabled={false}
                      />
                    </div>
                  ) : (
                    <div data-testid="test-case-cql-has-errors-message">
                      An error exists with the measure CQL, please review the
                      CQL Editor tab
                    </div>
                  ))}
                {rightPanelActiveTab === "highlighting" && (
                  <div
                    className="panel-content"
                    style={{ marginRight: "15px" }}
                  >
                    {executing ? (
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <MadieSpinner style={{ height: 50, width: 50 }} />
                      </div>
                    ) : (
                      <CalculationResults
                        mainCqlLibraryName={measure?.cqlLibraryName}
                        calculationResults={populationGroupResults}
                        calculationErrors={calculationErrors}
                        groupPopulations={groupPopulations}
                        cqlDefinitionCallstack={callstackMap}
                        includeSDE={measure?.testCaseConfiguration?.sdeIncluded}
                        includeRAV={measure?.testCaseConfiguration?.ravIncluded}
                        supplementalData={measure?.supplementalData}
                        riskAdjustments={measure?.riskAdjustments}
                        groups={measure?.groups}
                      />
                    )}
                  </div>
                )}
                {rightPanelActiveTab === "expectoractual" && (
                  <div className="panel-content">
                    <ExpectedActual
                      canEdit={canEdit}
                      groupPopulations={groupPopulations}
                      isTestCaseExecuted={!_.isNil(populationGroupResults)}
                      clearTestResults={() => {
                        setPopulationGroupResults(undefined);
                      }}
                      errors={formik.errors.groupPopulations}
                      groupsStratificationAssociationMap={stratificationsMap}
                      groups={measure.groups}
                      onChange={(
                        groupPopulations,
                        changedGroupId,
                        changedPopulation
                      ) => {
                        const stratOutput = triggerPopChanges(
                          groupPopulations,
                          changedGroupId,
                          changedPopulation,
                          measure?.groups
                        );
                        formik.setFieldValue(
                          "groupPopulations",
                          stratOutput as GroupPopulation[]
                        );
                      }}
                      onStratificationChange={(
                        groupPopulations,
                        changedGroupId,
                        changedStratification
                      ) => {
                        const stratOutput = triggerPopChanges(
                          groupPopulations,
                          changedGroupId,
                          changedStratification,
                          measure?.groups
                        );
                        formik.setFieldValue(
                          "groupPopulations",
                          stratOutput as GroupPopulation[]
                        );
                      }}
                    />
                  </div>
                )}
                {/*
            Independent views should be their own components when possible
            This will allow for independent unit testing and help render performance.
           */}

                {rightPanelActiveTab === "details" && (
                  <div className="panel-content">
                    {alert &&
                      (testCaseAlertToast ? (
                        <MadieAlert
                          type={alert?.status}
                          content={alert?.message}
                          alertProps={{
                            "data-testid": "create-test-case-alert",
                          }}
                          closeButtonProps={{
                            "data-testid": "close-create-test-case-alert",
                          }}
                        />
                      ) : (
                        <Alert
                          status={alert?.status}
                          role="alert"
                          aria-label="Create Alert"
                          data-testid="create-test-case-alert"
                        >
                          {alert?.message}
                          <button
                            data-testid="close-create-test-case-alert"
                            type="button"
                            tw="box-content h-4 p-1 ml-3 mb-1.5"
                            data-bs-dismiss="alert"
                            aria-label="Close Alert"
                            onClick={() => setAlert(null)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </Alert>
                      ))}

                    {/* TODO Replace with re-usable form component
               label, input, and error => single input control component */}

                    <div id="details-panel">
                      <TextField
                        placeholder="Test Case Title"
                        required
                        disabled={!canEdit}
                        label="Title"
                        id="test-case-title"
                        inputProps={{
                          "data-testid": "test-case-title",
                          "aria-describedby": "title-helper-text",
                          "aria-required": true,
                          required: true,
                        }}
                        helperText={formikErrorHandler("title")}
                        size="small"
                        error={
                          formik.touched.title && Boolean(formik.errors.title)
                        }
                        {...formik.getFieldProps("title")}
                        maxLength={250}
                      />
                      <div tw="mt-6">
                        <TextArea
                          placeholder="Test Case Description"
                          id="test-case-description"
                          data-testid="test-case-description"
                          disabled={!canEdit}
                          {...formik.getFieldProps("description")}
                          label="Description"
                          required={false}
                          inputProps={{
                            "data-testid": "test-case-description",
                            "aria-describedby": "description-helper-text",
                          }}
                          onChange={formik.handleChange}
                          value={formik.values.description}
                          error={
                            formik.touched.description &&
                            Boolean(formik.errors.description)
                          }
                          helperText={formikErrorHandler("description")}
                          maxLength={250}
                        />
                      </div>

                      <div tw="mt-6">
                        <TestCaseSeries
                          disabled={!canEdit}
                          value={formik.values.series}
                          onChange={(nextValue) =>
                            formik.setFieldValue("series", nextValue)
                          }
                          seriesOptions={seriesState.series}
                          sx={testCaseSeriesStyles}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Allotment.Pane>

            <Allotment.Pane minSize={4}>
              <div
                className={`validation-panel ${
                  showValidationErrors ? "open" : "closed"
                }`}
              >
                {showValidationErrors ? (
                  <>
                    <div className="flex justify-between items-center w-full mb-2">
                      <div className="validation-header">
                        <div className="header-left">
                          <ValidationStatusIcon
                            validationStatus={testCase?.validationStatus}
                          />

                          <span className="ml-2">
                            Validations ({validationErrors?.length || 0})
                          </span>
                        </div>

                        <Button
                          variant="action"
                          data-testid="hide-json-validation-errors-button"
                          onClick={() => {
                            setShowValidationErrors(false);
                            setTimeout(() => {
                              allotmentRef.current.resize([48, 48, 4]);
                            }, 0);
                          }}
                          className="validation-panel-toggle-button"
                          title="Close Panel"
                        >
                          <KeyboardTabIcon />
                        </Button>
                      </div>
                    </div>
                    <div
                      className="validation-content"
                      data-testid="json-validation-errors-list"
                    >
                      <ValidationPanel
                        testCase={testCase}
                        validationErrors={validationErrors}
                      />
                    </div>
                  </>
                ) : (
                  <div data-testid="closed-json-validation-errors-aside">
                    <div className="closed-header">
                      <Button
                        size="small"
                        data-testid="show-json-validation-errors-button"
                        onClick={() => {
                          setShowValidationErrors(true);
                          allotmentRef.current.resize([34, 33, 33]);
                        }}
                        className="validation-panel-toggle-button"
                        title={
                          testCase?.validationStatus
                            ? testCase?.validationStatus
                            : "Open Validations"
                        }
                      >
                        <ValidationStatusIcon
                          validationStatus={testCase?.validationStatus}
                        />
                      </Button>
                    </div>
                    <div className="closed-body"></div>
                  </div>
                )}
              </div>
            </Allotment.Pane>
          </Allotment>

          {/* button wrap in context */}
          <div tw="bg-gray-75 w-full sticky bottom-0 left-0 z-40">
            <div tw="flex items-center">
              <div tw="w-1/2 flex items-center px-2">
                {canEdit && <FileUploader onFileImport={updateTestCaseJson} />}
              </div>
              <div
                tw="w-1/2 flex justify-end items-center px-10 py-6"
                style={{ alignItems: "end" }}
              >
                <FormikProvider value={formikStu6Context}>
                  <Button
                    tw="m-2"
                    variant="outline"
                    onClick={() => setDiscardDialogOpen(true)}
                    data-testid="edit-test-case-discard-button"
                    disabled={!isModified()}
                  >
                    Discard Changes
                  </Button>
                  <Button
                    tw="m-2"
                    type="button"
                    onClick={calculate}
                    disabled={
                      !!measure?.cqlErrors ||
                      measure?.errors?.includes(
                        MeasureErrorType.MISMATCH_CQL_POPULATION_RETURN_TYPES
                      ) ||
                      _.isNil(measure?.groups) ||
                      measure?.groups.length === 0 ||
                      (!isJsonModified() &&
                        hasErrorSeverity(validationErrors)) ||
                      isEmptyTestCaseJsonString(editorVal) ||
                      !executionContextReady ||
                      executing
                    }
                    /*
                  if new test case
                    enable run button if json modified, regardless of errors
                 */
                    data-testid="run-test-case-button"
                  >
                    Run Test Case
                  </Button>
                  {canEdit && (
                    <Button
                      tw="m-2"
                      variant="cyan"
                      type="submit"
                      data-testid="edit-test-case-save-button"
                      disabled={!isModified()}
                    >
                      Save
                    </Button>
                  )}
                </FormikProvider>
              </div>
            </div>
          </div>
        </div>
        <MadieDiscardDialog
          open={discardDialogOpen}
          onClose={() => setDiscardDialogOpen(false)}
          onContinue={discardChanges}
        />
        <Toast
          toastKey="edit-action-toast"
          aria-live="polite"
          toastType={toastType}
          testId={toastType === "danger" ? "error-toast" : "success-toast"}
          closeButtonProps={{
            "data-testid": "close-toast-button",
          }}
          open={toastOpen}
          message={toastMessage}
          onClose={onToastClose}
          autoHideDuration={10000}
          // Override default styles to account for longer message
          sx={{
            overflow: "hidden",
            "& .MuiPaper-root": {
              backgroundColor: "#fff",
              padding: 0,
            },
            "& .MuiSnackbar-root": {
              overflow: "hidden",
              borderRadius: 4,
            },
            "& .MuiSnackbarContent-message": {
              padding: 0,
              flexGrow: 1,
              ".messageCont": {
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                justifyContent: "space-between",
                div: {
                  display: "flex",
                },
              },
            },
            ".toast": { minHeight: "80px" },
          }}
        />
      </TestCaseForm>
    </>
  );
};

export default EditTestCase;
